/* global $:false Sentry:false */

import {
   DateF,
   format as formatDate,
   getTimestampFor as getTimestampForDate
}  from '../fields/recordm.fields.date.js';
import {
   DateTime,
   format as formatDateTime,
   getTimestampFor as getTimestampForDatetime
} from '../fields/recordm.fields.datetime.js';
import {
   NumberF,
   format as formatNumber
} from '../fields/recordm.fields.number.js';
import {
   default as Tasks,
   TasksGroupEdit
} from '../fields/recordm.fields.tasks.js';

import AutoLib from '../autolibs/recordm.auto.js';
import Create from '../fields/recordm.fields.create.js';
import DuplicateFieldsPresenter from './recordm.presenter.instance.duplicatefield.js';
import ExtRef from '../fields/recordm.fields.extref.js';
import FieldView from '../view/recordm.view.instance.field.js';
import File from '../fields/recordm.fields.file.js';
import InstanceM from '../model/recordm.model.entity.instance.js';
import Link from '../fields/recordm.fields.link.js';
import Markdown from '../fields/recordm.fields.markdown.js';
import Multiple from '../fields/recordm.fields.select.multiple.js';
import Radio from '../fields/recordm.fields.select.radio.js';
import Ref from '../fields/recordm.fields.ref.js';
import References from "../fields/recordm.fields.references";
import Select from '../fields/recordm.fields.select.js';
import Time from '../fields/recordm.fields.time.js';
import * as Visibility from '../helpers/visibility.js';

import marked from 'marked';

import { Keywords, extendDef, parseOptions } from '../fields/recordm.fields.js';

import ui from '@cob/ui-genesis/cob.ui.js';
import utils from '@cob/ui-genesis/cob.utils.js';

/**
 * * `internal`: an internal PubSub to be used for events that only matter to the instance.
 * * `instance`: an IndexedInstanceM (maybe just an InstanceM?)
 * * `field`: an object similar to the field JSON returned by the server
 * * `parent`: an optional parent FieldPresenter
 * * `opts`:
 *    * `create`: is part of the creation of a new Instance
 *    * `edit`: is part of editing a new Instance (may be a new field)
 *    * `groupEditMode`: in group edit mode
 *    * `validate`: true in edit and false in view or groupEditMode
 *    * `isNewDuplicate`: used to init the $group name or $ref placeholders (todo jone e os outros? $references pelo menos)
 *    * `placeholders`: used to copy placeholder information between duplicates (it's the same for all)
 * * `ignoreDefaults`:
 * * `callbacks`:
 *    * `updateVisibility`: only passed from parent to child fields, as a way to propagate visibility changes up the hierarchy.
 */
const FieldPresenter = function(core, internal, instance, field, parent, opts, ignoreDefaults, clipboard, callbacks) {

    var _fieldPs = [];
    var _dupPs = [];
    var _auto;
    var _expand;
    var _mark;
    var _readonly;
    var _readonlyConf;
    var _autoFunction;
    var _isGroup, _isReferences;
    var _isRef, _isExtRef;
    var _refModel;
    var _placeholders;
    var _isCreateLinkField;

    var view;
    var _fieldComponent;

    var _defaultValue;

    var _visible = true;
    var _newlyVisible = false;
    var _previousChildrenVisibility = false;
    var _isRootField = (parent === null
        || parent.field === undefined
        || parent.field.fieldDefinition === null
        || parent.field.fieldDefinition === undefined);

    var _clone = function() {

        var fieldCopy = _getClone(true);
        if (_isRootField) {
            internal.publish("instance-root-field-collection-increment-" + field.fieldDefinition.id, fieldCopy);

        } else {
            internal.publish("field-collection-increment-" + parent.field.id + "-" + field.fieldDefinition.id, fieldCopy);
        }
    };

    var _getClone = function(isRoot, newParent) {
        var fieldCopy = {};

        fieldCopy.parent = isRoot ? field.parent : newParent;

        if (field.fields) fieldCopy.fields = [];

        fieldCopy.fieldDefinition = field.fieldDefinition;
        if (isRoot) fieldCopy.duplicate = true;
        fieldCopy.id = core.locallyUniqueId();

        _fieldPs.forEach(function(el) {
            fieldCopy.fields.push(el.getClone(false, fieldCopy.id));
        });

        _dupPs.forEach(function(el) {
            //todo jone isto também tem que levar com o novo parent?
            el.fillClones(fieldCopy);
        });

        //sort the fieldCollection by the def id and instance id
        if (fieldCopy.fields) {
            fieldCopy.fields.sort(function(me, other) {
                var diff = me.fieldDefinition.order - other.fieldDefinition.order;
                if (diff === 0) {
                    diff = other.id - me.id;
                }
                return diff;
            });
        }

        return fieldCopy;
    };

    /* eslint-disable-next-line complexity */
    var _extendField = function() {

        var fieldDesc = field.fieldDefinition.description;
        const def = field.fieldDefinition;

        extendDef(def);

        // If the opts['edit'] is a function then it takes precedence of everything else.
        // See recordm.GroupEditInstanceP
        if (typeof opts.edit === 'function') {
            _readonly = !opts.edit(field);

        } else {
            _readonly = !InstanceM.canEditField(core, field, instance.jsonDefinition) || !opts.edit;
        }

        def.readOnly = _readonly;

        if (fieldDesc) {

            const _hasDefault = def.isA(Keywords.Default);
            _defaultValue = _hasDefault ? def.conf(Keywords.Default).args.value : undefined;
            // we don't want to use the default value in some situations
            const usableDefaultValue = (opts.create && !ignoreDefaults && !_readonly) ? _defaultValue : undefined;

            //attribute readonly is to be only used in the UI (NOT to be validated in the backend)
            // Use case is for fields that are filled with the Clipboard values, and therefore changed in the user
            // session, but are not supposed to be changed manually by the user
            const isUIReadonly = def.isA(Keywords.Readonly);
            if(isUIReadonly){
                def.readOnly = true;
                const args = def.conf(Keywords.Readonly).args;
                if(args.condition){
                    // should be field=value or field!=value, if any
                    const operator = args.condition.indexOf('!=') > -1 ? '!=' : '='
                    const parts = args.condition.split(operator);
                    const targetFieldName = parts[0].trim();
                    _readonlyConf = {
                        fieldName: targetFieldName,
                        condition: operator,
                        value: parts[1]?.trim()
                    }
                    internal.subscribe('field-value-change-byName-' + encodeURIComponent(targetFieldName), _applyReadonly);

                } else {
                    _readonlyConf = { }
                }
            }

            const hasHelp = def.isA(Keywords.Help);
            if (hasHelp) {
                def.help = def.conf(Keywords.Help).args.join('');
            }

            const hasCustomStyles = def.isA(Keywords.Style);
            if (hasCustomStyles) {
                def.styles = def.conf(Keywords.Style).args.map(
                    function(cssClass) {
                        return "custom-" + cssClass.trim();
                    }).join(' ');
            }

            def.displayDescription = def.configuration.description ?? '';
            // if it's available (and it should because the lib should be included) convert from markdown
            if (typeof marked === "function") def.displayDescription = marked(def.displayDescription);

            const isSelectField = def.isA(Keywords.Select);
            const isRadioField = def.isA(Keywords.Radio);
            const isMultipleField = def.isA(Keywords.Multiple);
            if (isSelectField) {
                var _fieldConfs;

                def.hasOptions = true;
                def.options = def.conf(Keywords.Select).args;
                if (!ignoreDefaults) _defaultValue = def.conf(Keywords.Select)['default'] ?? _defaultValue;

                _fieldConfs = {
                    id: field.id,
                    readonly: def.readOnly,
                    defaultValue: usableDefaultValue,
                    allowEmptyOption: true,
                    cssClasses: "select-field-def-" + def.id, //useful for customization css
                    valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                            id: def.id,
                            value: _fieldComponent.getValue()
                        });
                    }
                };

                if(isRadioField){
                    _fieldComponent = new Radio(core, def.options, _fieldConfs);

                }else if(isMultipleField){
                    _fieldComponent = new Multiple(core, def.options, _fieldConfs);

                }else{
                    _fieldComponent = new Select(core, def.options, _fieldConfs);
                }

                if (opts.groupEditMode) {
                    // when editing in group we don't want the default value.
                    field.value = undefined;

                } else {
                    _fieldComponent.setValue(field.value);
                }
            }

            const anyAuto = def.any(/Auto.*/);
            if (anyAuto) {
                _auto = {
                    type: anyAuto,
                    args: def.conf(anyAuto).args
                }
            }

            const _linkField = def.isA(Keywords.Link);
            if (_linkField) {
                def.isLink = true;

                const linkOptions = def.conf(Keywords.Link).args.options;
                let doEncode = true;
                if(linkOptions){
                    // should be encode:false, if any
                    const parts = linkOptions.split(':');
                    if(parts[0].trim() === 'encode' && parts[1].trim() === 'false'){
                        doEncode = false;
                    }
                }

                _fieldComponent = new Link(core, {
                    id: field.id,
                    initialHref: field.value,
                    text:def.name,
                    encode: doEncode
                });
            }

            const _fileField = def.isA(Keywords.File) || def.isA(Keywords.ExtFile);
            if (_fileField) {
                _fieldComponent = new File(core,
                    'recordm/instances/' + instance.attachmentPath + '/files/' + def.id,
                    {
                        id: field.id,
                        readonly: def.readOnly,
                        valueChanged: function() {
                            _setValue(_fieldComponent.getValue(), {
                                id: def.id,
                                value: _fieldComponent.getValue()
                            });
                        }
                    }
                );

                _fieldComponent.setValue(field.value);
            }

            const _textField = def.isA(Keywords.Text);
            if (_textField) {
                def.text = true;
            }

            const _hasExpand = def.isA(Keywords.Expanded);
            if (_hasExpand) {
                const args = def.conf(Keywords.Expanded).args;
                if(args.condition){
                    // should be field=value or field!=value, if any
                    const operator = args.condition.indexOf('!=') > -1 ? '!=' : '='
                    const parts = args.condition.split(operator);
                    const targetFieldName = parts[0].trim();
                    _expand = {
                        fieldName: targetFieldName,
                        condition: operator,
                        value: parts[1]?.trim()
                    }
                    internal.subscribe('field-value-change-byName-' + encodeURIComponent(targetFieldName), _applyExpands);

                } else {
                    _expand = { }
                }
            }

            const _hasMarkIf = def.isA(Keywords.MarkedIf);
            if (_hasMarkIf) {
                const args = def.conf(Keywords.MarkedIf).args;
                if(args.condition){
                    // should be field=value or field!=value, if any
                    const operator = args.condition.indexOf('!=') > -1 ? '!=' : '='
                    const parts = args.condition.split(operator);
                    const targetFieldName = parts[0].trim();
                    _mark = {
                        fieldName: targetFieldName,
                        condition: operator,
                        value: parts[1]?.trim()
                    }
                    internal.subscribe('field-value-change-byName-' + encodeURIComponent(targetFieldName), _applyMarkIf);

                } else {
                    _mark = { }
                }
            }

            const isTimeField = def.isA(Keywords.Time);
            if (isTimeField) {
                _fieldComponent = new Time(core, {
                    id: field.id,
                    fieldDefinition: field.fieldDefinition,
                    readonly: def.readOnly,
                    valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                            id: def.id,
                            value: _fieldComponent.getValue()
                        });
                    }
                });

                _fieldComponent.setValue(field.value || usableDefaultValue);
            }

            const isDateField = def.isA(Keywords.Date);
            if (isDateField) {
                def.isDate = true;
                _fieldComponent = new DateF(core, {
                    id: field.id,
                    fieldDefinition: field.fieldDefinition,
                    readonly: def.readOnly,
                    valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                            id: def.id,
                            value: _fieldComponent.getValue()
                        });
                    }
                });

                _fieldComponent.setValue(field.value || getTimestampForDate(usableDefaultValue));
            }

            const isDateTimeField = def.isA(Keywords.DateTime);
            if (isDateTimeField) {
                def.isDatetime = true;
                _fieldComponent = new DateTime(core, {
                    id: field.id,
                    fieldDefinition: field.fieldDefinition,
                    readonly: def.readOnly,
                    valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                            id: def.id,
                            value: _fieldComponent.getValue()
                        });
                    }
                });

                _fieldComponent.setValue(field.value || getTimestampForDatetime(usableDefaultValue));
            }

            const isNumber = def.isA(Keywords.Number);
            if (isNumber) {
                def.isNumber = true;
                def.decimalPlaces = def.conf(Keywords.Number).args.decimal_places ?? '2';

                _fieldComponent = new NumberF(core, {
                    id: field.id,
                    fieldDefinition: field.fieldDefinition,
                    readonly: def.readOnly,
                    decimalPlaces: def.decimalPlaces, //number of decimal places to support (autonumeric.js prop)
                    allowDecimalPadding: (def.conf(Keywords.Number).args.decimal_places > 0),
                    valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                            id: def.id,
                            value: _fieldComponent.getValue()
                        });
                    }
                });

                _fieldComponent.setValue(field.value || usableDefaultValue);
            }

            _isRef = def.isA(Keywords.Reference);
            if (_isRef) {
                def.isRef = true;
                const args = def.conf(Keywords.Reference).args;
                const options = parseOptions(args.options);
                field.refFilter = args.query;

                _placeholders = FieldPresenter.getPlaceholders(field.refFilter);

                if(_placeholders && !def.readOnly){
                    _parseFieldPlaceholders(field, _placeholders, _updateRefPlaceholders);
                }

                _fieldComponent = new Ref(core, args.definition, {
                    id: field.id,
                    fieldDefinition: field.fieldDefinition,
                    readonly: def.readOnly,
                    query: field.refFilter,
                    sort: options.sort,
                    maxRows: Number(options.size),
                    valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                            id: def.id,
                            value: _fieldComponent.getValue(),
                            refmodel: _fieldComponent.getModel()
                        });
                    }
                });

                _fieldComponent.setValue(field.value || usableDefaultValue, field.label);
            }

            _isExtRef = def.isA(Keywords.ExternalReference);
            if (_isExtRef) {
                const args = def.conf(Keywords.ExternalReference).args;
                field.extRefSearchTemplate = args.query;

                _placeholders = FieldPresenter.getPlaceholders(field.extRefSearchTemplate);

                if(_placeholders && !def.readOnly){
                    _parseFieldPlaceholders(field, _placeholders, _updateExtRefPlaceholders);
                }

                _fieldComponent = new ExtRef(
                    core, args.product, field.extRefSearchTemplate,
                    {
                        id: field.id,
                        fieldDefinition: field.fieldDefinition,
                        readonly: def.readOnly,
                        valueChanged: function() {
                            // if the value changed, it must be uptodate
                            field.upToDate = true;
                            view.showOutdated(false);
                            _setValue(_fieldComponent.getValue(), {
                                id: def.id,
                                value: _fieldComponent.getValue(),
                                refmodel: _fieldComponent.getModel()
                            });
                        }
                    });

                _fieldComponent.setValue(field.value || usableDefaultValue, field.label);
            }

            const isTasks = def.isA(Keywords.Tasks);
            if(isTasks) {
               def.tasks = true;
               if(opts.groupEditMode){
                  _fieldComponent = new TasksGroupEdit(core, instance, {
                     id: field.id,
                     readonly: def.readOnly
                  });
               } else {
                  _fieldComponent = new Tasks(core, instance, {
                     id: field.id,
                     readonly: def.readOnly,
                     valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                           type: 'tasks',
                           id: def.id,
                           value: _fieldComponent.getTasks()
                        });
                     }
                  });
               }

               if(typeof _fieldComponent.setTasks === 'function' && field.tasks){
                  _fieldComponent.setTasks(field.tasks);
               } else {
                  _fieldComponent.setValue(field.value || usableDefaultValue);
               }
            }


            const isMarkdown = def.isA(Keywords.Markdown);
            if (isMarkdown) {
                def.markdown = true;

                _fieldComponent = new Markdown(core, {
                    id: field.id,
                    fieldDefinition: field.fieldDefinition,
                    readonly: def.readOnly,
                    valueChanged: function() {
                        _setValue(_fieldComponent.getValue(), {
                            id: def.id,
                            value: _fieldComponent.getValue()
                        });
                    }
                });

                _fieldComponent.setValue(field.value || usableDefaultValue);
            }

            _isGroup = def.isA(Keywords.Group);
            if(_isGroup){
                _placeholders = FieldPresenter.getPlaceholders(def.name);
                if(_placeholders){
                    _parseFieldPlaceholders(field, _placeholders, _updateGroupPlaceholders);
                }
            }


            _isCreateLinkField = def.isA(Keywords.Create);
            if (_isCreateLinkField) {
                const defName = def.conf(Keywords.Create).args[0];
                const args = def.conf(Keywords.Create).args.slice(1);
                const fields = args.filter((f) => !f.startsWith('['));
                const options = parseOptions(args.find((f) => f.startsWith('[')));

                def.isLink = true; // let it also be isLink to apply link style rules in the template
                _fieldComponent = new Create(
                    core,
                    defName,
                    fields,
                    {
                        ...options,
                        id: field.id,
                        text: def.name,
                        getValueOf: function(fieldName) {
                            var targetFieldValue;

                            if(fieldName.toLowerCase() === "id"){
                                targetFieldValue = String(instance.id);

                            } else {
                                const target = _findTargetField(fieldName);
                                // someday jone - porque é que os IDs são números? Não dá para fazer aritmética com eles
                                if (Number.isNaN(target.id)){
                                    // someday jone - para quê os dois tipos de alertas?
                                    window.console.error(
                                       '$create expression malformed on field "' + def.name +
                                       '" - no field exists with the name "' + fieldName + '"');
                                    ui.notification.showError(
                                        core.translateString("recordm.error.codes", "create-link.field-not-exists")
                                            .replace("$[field]", def.name)
                                            .replace("$[bad-field]", fieldName),
                                        true
                                    );
                                }
                                const targetValues = _readTargetField(target);

                                targetFieldValue = targetValues.rawValue;
                            }

                            return targetFieldValue;


                        }
                    });
            }

            _isReferences = def.isA(Keywords.References);

            if (_isReferences) {
                def.references = true;

                const args = def.conf(Keywords.References).args;
                const searchField = args.field;
                let searchFieldValue;
                let activeVisualizationName;
                const options = parseOptions(args.options);

                if(options !== null) {
                    activeVisualizationName = options.view;

                    if(options.value){
                        field.refFilter = options.value;
                        searchFieldValue = options.value;
                    }
                }

                _placeholders = (field.refFilter !== undefined
                    ? FieldPresenter.getPlaceholders(field.refFilter)
                    : undefined);

                if(_placeholders){
                    _parseFieldPlaceholders(field, _placeholders, _updateReferencesPlaceholders);
                }

                const finalOptions = {
                    activeVisualizationName: activeVisualizationName,
                    createMode: opts.create,
                    groupEditMode: opts.groupEditMode,
                    hasPlaceholders: _placeholders
                };
                utils.extend(finalOptions, options, true);

                _fieldComponent = new References(core, instance.id,
                    args.definition, searchField, searchFieldValue,
                    finalOptions
                );
            }

            if(_fieldComponent && typeof _fieldComponent.setPermissions === 'function' && field.permissions){
               _fieldComponent.setPermissions(field.permissions);
            }
        } else {
            field.fieldDefinition.displayDescription = '';
        }

        field.fieldDefinition.calculatedRequired = !field.fieldDefinition.readOnly &&
                                                   field.fieldDefinition.required &&
                                                   opts[field.fieldDefinition.required] &&
                                                   opts.validate;

        //hack: because there is no value property when there is no value
        if (!field.value) {
            field.value = '';
        }

        //setup visibility
        if(field.fieldDefinition.visibilityCondition){
            field.viz = Visibility.hydrate(field, {
                parent: {
                   getValue: () => _readTargetField({id: parent.field.id}).value ?? ""
                },
                findValue: (name) => {
                    if(name === 'id') {
                        return instance.id;
                    }
                    const target = _findTargetField(name);
                    window.console.debug('JN', 'viz', 'find by name', name, target);
                    if(target) return _readTargetField(target).value ?? "";

                    return "";
                }
            });
        }
    };

    var _parseFieldPlaceholders = function(field, placeholders, onFieldChangeCallback) {
        //We have to maintain a placeholders map so we can change the groupName every time one of its placeholders change.
        //With one time placeholder replacement we would lose the placeholder format __x__ and we wouldn't be able to change it again
        field.placeholders = {};

        placeholders.forEach( (placeholder) => {
            field.placeholders[placeholder] = placeholder;

            // The "__id__" placeholder should only be replaced once, on the _initPlaceholders function,
            // because it refers to the instance's id, which never changes
            if(placeholder !== "__id__"){
                const name = FieldPresenter.resolvePlaceholder(placeholder);
                internal.subscribe('field-value-change-byName-' + encodeURIComponent(name), onFieldChangeCallback);
            }
        });
    };

    var _updateGroupPlaceholders = function(evt, data) {
        //update placeholder new value and recalculate group name
        field.placeholders['__' + data.name + '__'] = (data.value || '__' + data.name + '__');

        //recalculate the whole Name
        var updatedGroupName = field.fieldDefinition.name;
        utils.forEachOwn(field.placeholders, function(key) {
            updatedGroupName = updatedGroupName.replaceAll(key, '<i class="group-placeholder">' + field.placeholders[key] + '</i>')
        });

        view.setGroupFieldName(updatedGroupName);
    };

    var _updateReferencesPlaceholders = function(evt, data) {
        //update placeholder new value and recalculate field filter
        field.placeholders['__' + data.name + '__'] = (data.value || '__' + data.name + '__');

        //recalculate the field filter
        let updatedRefFilter = field.refFilter;

        utils.forEachOwn(field.placeholders, function(key) {
            updatedRefFilter = updatedRefFilter.replaceAll(key, field.placeholders[key]);
        });

        const hasPlaceholders = FieldPresenter.getPlaceholders(updatedRefFilter);

        if(!hasPlaceholders){
            _fieldComponent.setSearchValue(updatedRefFilter);
        }
    };

    var _updateRefPlaceholders = function(evt, data) {
        //update placeholder new value and recalculate field filter
        field.placeholders['__' + data.name + '__'] = (data.value || '__' + data.name + '__');

        //recalculate the field filter
        var updatedRefFilter = field.refFilter;

        utils.forEachOwn(field.placeholders, function(key) {
            updatedRefFilter = updatedRefFilter.replaceAll(key, encodeURIComponent(field.placeholders[key]));
        });

        _fieldComponent.setQuery(updatedRefFilter);
    };

    var _updateExtRefPlaceholders = function(evt, data) {
        //update placeholder new value and recalculate field filter
        field.placeholders['__' + data.name + '__'] = (data.value || '__' + data.name + '__');

        //recalculate the field filter
        var updatedSearchTemplate = field.extRefSearchTemplate;

        utils.forEachOwn(field.placeholders, function(key) {
            updatedSearchTemplate = updatedSearchTemplate.replaceAll(key, encodeURIComponent(field.placeholders[key]));
        });

        _fieldComponent.setUrlTemplate(updatedSearchTemplate);
    };

    var _initGroupPlaceholders = function() {

        //calculate the group Name
        var updatedGroupName = field.fieldDefinition.name;

        _initPlaceholders();

        utils.forEachOwn(field.placeholders,function(key){
            updatedGroupName  = updatedGroupName.replaceAll(key,'<i class="group-placeholder">'+field.placeholders[key]+'</i>')
        });

        view.setGroupFieldName(updatedGroupName);
    };

    const _initRefsPlaceholders = function() {

        //calculate the ref field search filter
        let updatedRefFilter = field.refFilter;

        _initPlaceholders();

        utils.forEachOwn(field.placeholders,function(key){
            updatedRefFilter  = updatedRefFilter.replaceAll(key, encodeURIComponent(field.placeholders[key]));
        });

        const hasPlaceholders = FieldPresenter.getPlaceholders(updatedRefFilter);

        if(!hasPlaceholders){
            _fieldComponent.setSearchValue(updatedRefFilter);
        }
    };

    var _initRefPlaceholders = function() {

        //calculate the ref field search filter
        var updatedRefFilter = field.refFilter;

        _initPlaceholders();

        utils.forEachOwn(field.placeholders,function(key){
            updatedRefFilter  = updatedRefFilter.replaceAll(key, field.placeholders[key]);
        });

        _fieldComponent.setQuery(updatedRefFilter);
    };

    var _initExtRefPlaceholders = function() {
        //calculate the ref field search filter
        var updated = field.extRefSearchTemplate;

        _initPlaceholders();

        utils.forEachOwn(field.placeholders,function(key){
            updated  = updated.replaceAll(key, encodeURIComponent(field.placeholders[key]));
        });

        _fieldComponent.setUrlTemplate(updated);
    };

    var _initPlaceholders = function(){
        utils.forEachOwn(field.placeholders,function(key){
            var targetValue;

            //If it's a duplicated field copy the original field placeholders
            if(field.duplicate){
                targetValue = opts.placeholders[key];

            } else {
                const fieldName = FieldPresenter.resolvePlaceholder(key);

                // Allow the usage of the current instance's id as a placeholder (__id__)
                // The instance id never changes so we only update this placeholder here
                if(fieldName === "id"){
                    targetValue = instance.id;
                } else{
                    const target = _findTargetField(fieldName);
                    const targetValues = _readTargetField(target);
                    targetValue = targetValues.value;
                }
            }

            field.placeholders[key] = (targetValue || key);
        });
    };


    var _updateVisibility = function() {
        var _currentlyVisible = !field.off && field.viz.test();
        var _changedVisibility = _currentlyVisible ^ _visible;
        _newlyVisible = _currentlyVisible && !_visible;

        _visible = _currentlyVisible;

        //hack jone para quando duplicamos campos os novos replicarem a visibilidade do original
        field.fieldDefinition.hackVisible = _visible;

        // para preenchermos os campos que ficaram visíveis com os valores default
        if(_newlyVisible && !_readonly && Boolean(_defaultValue)) _fillWithDefaultValue();

        if (_visible) {
            view.showSelf();
        } else {
            view.hideSelf();
        }

        if(_changedVisibility && _visible && callbacks && callbacks.updateVisibility) callbacks.updateVisibility();

    };

    var _updateChildrenState = function() {
        var updatedChildrenVisibility = _hasChildrenVisible();
        var newlyVisibleChildren = _hasChildrenNewlyVisible();

        if ((updatedChildrenVisibility && !_previousChildrenVisibility) || newlyVisibleChildren) {
            view.expandFields();
            view.enableToggleButton();

        } else if (!updatedChildrenVisibility && _previousChildrenVisibility) {
            view.colapseFields();
            view.disableToggleButton();
        }

        _previousChildrenVisibility = updatedChildrenVisibility;
    };

    var _hasChildrenVisible = function() {
        var someChildVisible = false;

        _fieldPs.forEach(function(fieldP) {
            someChildVisible = someChildVisible || fieldP.isVisible();
        });

        _dupPs.forEach(function(dupP) {
            someChildVisible = someChildVisible || dupP.isVisible();
        });

        return someChildVisible;
    };

    var _hasChildrenNewlyVisible = function() {
        var someChildNewlyVisible = false;

        _fieldPs.forEach(function(fieldP) {
            someChildNewlyVisible = someChildNewlyVisible || fieldP.isNewlyVisible();
        });

        _dupPs.forEach(function(dupP) {
            someChildNewlyVisible = someChildNewlyVisible || dupP.isNewlyVisible();
        });

        return someChildNewlyVisible;
    };

    var _fillWithDefaultValue = function(){
       // fields that had values don't revert to default
       if(field.value) return;

       let value = _defaultValue;

       // these fields have a default of "now", and we do the conversion only on demand to have the freshest "now".
       if(field.fieldDefinition.isA(Keywords.Date)){
          value = getTimestampForDate(_defaultValue);

       } else if(field.fieldDefinition.isA(Keywords.DateTime)){
          value = getTimestampForDatetime(_defaultValue);
       }

       //someday jone parece estranho ter de fazer os dois, mas não tenho tempo agora para investigar
       view.setValue(value);
       _setValue(value);
    }

    var _validateSelf = function() {
        const ok = !_visible ||
                 field.fieldDefinition.readOnly || !field.fieldDefinition.required || !opts[field.fieldDefinition.required] ||
                 (view.getValue() !== undefined && view.getValue() !== '');

        if (ok) {
            _fieldComponent ? _fieldComponent.clearError() : view.clearErrors();
        } else {
            _fieldComponent ? _fieldComponent.showError() : view.markRequiredError();
        }

        return ok;
    };

    // someday jone isto devia fazer parte do field (model)
    // Returns the most immediate Ancestor that matches the name (ignore the rest if more than one)
    const _findAncestor = function(name, parent){
        if(parent.field && parent.field.fieldDefinition.name === name) return parent;

        // it means the parent is the InstanceP, nothing found
        if(!parent.isField) return;

        return _findAncestor(name, parent.getParent());
    };

    // someday jone isto devia fazer parte do field (model)
    // Returns the closest ancestor (or sibling)
    const _findAncestorOrSibling = function(name, parent){
        // it means the parent is the InstanceP, nothing found
        if(!parent.isField) return;

        if(parent.field && parent.field.fieldDefinition.name === name) return parent;

        if(parent.isField){
            const sibling = parent.getChildPs().find((f) => f.field.fieldDefinition.name == name )
            if(sibling) return sibling;
        }

        return _findAncestorOrSibling(name, parent.getParent());
    };


    // Returns the first Descendent that matches the name (Breadth First Search) (ignore the rest if more than one)
    const _findDescendent = function (name, childsPs) {
        if (childsPs.length) {
            let target = childsPs.find((f) => f.field.fieldDefinition.name == name )
            if(target) {
                return target
            } else {
                for(const childP of childsPs) {
                    target = _findDescendent(name,childP.getChildPs())
                    if(target) return target
                }
            }
        }
    }

    // Returns the first sibling that matches the name (ignore the rest if more than one)
    const _findSibling = function (name) {
        if(parent.isField){
            return _findDescendent(name, parent.getChildPs());
        }

        // parent = dummy field with findSibling(name) method (see the InstanceP._init function)
        return parent.findSibling(name)[0];
    }

    // Returns the first field that matches the name (ignore the rest if more than one)
    const _findAny = function(name){
        let root = parent;

        // find the closest root field
        while(root.isField) root = root.getParent();

        // root's parent = dummy field with findSibling(name) method (see the InstanceP._init function)
        return root.findSibling(name)[0];
    };

    const _findTargetField = function(name){
        const target = _findAncestor(name, parent)
                       ?? _findDescendent(name, _fieldPs)
                       ?? _findSibling(name)
                       ?? _findAny(name);

        const eql2BE = _findAncestorOrSibling(name, parent)
                       ?? _findDescendent(name, _fieldPs)
                       ?? _findAny(name);

        if(target !== eql2BE){
            const msg = `_findTargetField impls got different results.{{name: ${name}, A: ${target?.field?.id}, B: ${eql2BE?.field?.id}}}`;
            window.console.warn('JN', msg)
            if(typeof Sentry !== 'undefined') Sentry.captureMessage(msg);
        }

        if(target) return {
            field: target.field,
            defId: target.field.fieldDefinition.id,
            id: target.field.id
        };

        window.console.log("we couldn't find a field named " + name + ". Maybe it's not visible?")
        return { }
    };

    const $instance = $(`div#service-${instance.id}`);

    // call with the results of _findTargetField
    const _readTargetField = function(target){
        let $targetField = $instance.find('#f' + target.id);
        let targetValue = $targetField.val();
        const targetRawValue = $targetField.attr('raw-value') || targetValue;

        if($targetField.length === 0){ //field is of another type

            $targetField = $instance.find('.radiogroup_'+target.id);
            if ($targetField.length > 0){
                targetValue = Radio.getValueOf($instance, 'radiogroup_'+target.id);

            }else{
                $targetField = $instance.find('.checkboxgroup_'+target.id);
                if($targetField.length > 0) {
                    targetValue = Multiple.getValueOf($instance, 'checkboxgroup_'+target.id);

                } else {
                    window.console.warn('No target field exists (better debug this !)', target);
                }
            }
        }

        return {
            value: targetValue,
            rawValue: targetRawValue
        }
    };

    var _registerAutoFunction = function() {
        _autoFunction = AutoLib.getFunction(internal, _auto);

        _autoFunction.init(instance, {
            find: _findTargetField,
            read: _readTargetField,
            onChange: _autoFunctionValueChanged
        });

        view.setValue(field.value);

        _applyAutoFunction();
    };

    var _autoFunctionValueChanged = function(_e, valueChange) {
        view.clearErrors();

        var theValue, theRawValue;
        if(valueChange.refmodel){
            theValue = valueChange.refmodel;
        } else if(valueChange.fieldType === 'datetime'){
            theValue = formatDateTime(valueChange.value);
        } else if(valueChange.fieldType === 'date'){
            theValue = formatDate(valueChange.value);
        } else if(valueChange.fieldType === 'number'){
            theValue = formatNumber(valueChange.value, false, valueChange.decimalPlaces, core.getLocale());
        } else{
            theValue = valueChange.value;
        }

        theRawValue = valueChange.refmodel? theValue : valueChange.value;

        // apenas acontece no AutoNetworkOffset, talvez seja de remover (o apply() já faz)
        var setArgsResult = _autoFunction.setArgs(valueChange.fieldId, theValue, theRawValue);

        if (setArgsResult) {
            view.showError(setArgsResult)
        }
        try {
            _applyAutoFunction();
        } catch (e) {
            ui.notification.showNotification(
                'error',
                core.translateString("recordm.error.codes", "auto.overflow").replace("$[field]", field.fieldDefinition.name),
                false);
            window.console.log("Auto field generation exception: :");
            window.console.log(e);
        }
    };

    var _applyExpands = function() {
        var targetFieldName =  _expand.fieldName,
            condition =  _expand.condition,
            conditionalValue =  _expand.value,
            targetFieldValue;

        if (targetFieldName) {
            if(targetFieldName.toLowerCase() === "id"){
                targetFieldValue = String(instance.id);

            } else {
                const target = _findTargetField(targetFieldName);
                const targetValues = _readTargetField(target);

                targetFieldValue = targetValues.value;
            }

            if (targetFieldValue) {
                if (condition === '=') {
                    if (targetFieldValue === conditionalValue.trim()) {
                        view.expandFields();
                    }
                } else {
                    if (targetFieldValue !== conditionalValue.trim()) {
                        view.expandFields();
                    }
                }
            }

        } else {
            view.expandFields();
        }
    };

    var _applyMarkIf = function() {
        var targetFieldName =  _mark.fieldName,
            condition =  _mark.condition,
            conditionalValue =  _mark.value,
            targetFieldValue;

        if (targetFieldName) {
            if(targetFieldName.toLowerCase() === "id"){
                targetFieldValue = String(instance.id);

            } else {
                const target = _findTargetField(targetFieldName);
                const targetValues = _readTargetField(target);

                targetFieldValue = targetValues.value;
            }

            view.unmarkField();
            if (targetFieldValue) {
                if (condition === '=') {
                    if (targetFieldValue === conditionalValue.trim()) {
                        view.markField();
                    }
                } else {
                    if (targetFieldValue !== conditionalValue.trim()) {
                        view.markField();
                    }
                }
            }
        }
    };

    var _applyReadonly = function() {
        if (_readonlyConf && _readonlyConf.fieldName) {
            const targetFieldName = _readonlyConf.fieldName,
                condition = _readonlyConf.condition,
                conditionalValue = _readonlyConf.value;
            let targetFieldValue;

            if(targetFieldName.toLowerCase() === "id"){
                targetFieldValue = String(instance.id);

            } else {
                const target = _findTargetField(targetFieldName);
                const targetValues = _readTargetField(target);

                targetFieldValue = targetValues.value;
            }

            view.enableSelf();
            if (condition === '=') {
                if (targetFieldValue === conditionalValue.trim()) {
                    view.disableSelf();
                }
            } else {
                if (targetFieldValue !== conditionalValue.trim()) {
                    view.disableSelf();
                }
            }
        } else {
            view.disableSelf();
        }
    };

    const debounced = $.debounce(function(){
        _updateVisibility();
    }, 100);

    const _initViz = function() {
        window.console.debug('JN', 'viz', '_initViz', field.fieldDefinition.name)
        _updateVisibility();
        // after the first pass, we subscribe to updates
        for(const evName of field.viz.events()){
            window.console.debug('JN','viz', 'subscribe', evName)
            internal.subscribe(evName, debounced);
        }
    }


    var _setValue = function(newValue, data) {
        var publishInfo = {
           id: field.fieldDefinition.id,
           value: newValue,
           refmodel: _refModel
        };

        if(field.fieldDefinition.isDatetime){
            publishInfo.fieldType = 'datetime';

        } else if(field.fieldDefinition.isDate){
            publishInfo.fieldType = 'date';

        } else if(field.fieldDefinition.isNumber){
            publishInfo.fieldType = 'number';
            publishInfo.decimalPlaces = field.fieldDefinition.decimalPlaces;
        }

        if(data){
            data.fieldType = data.fieldType || publishInfo.fieldType;
            publishInfo = data;
        }

        internal.publish('field-value-change-' + field.id, newValue);

        internal.publish('field-value-change-byName-' + encodeURIComponent(field.fieldDefinition.name),
            {"name": field.fieldDefinition.name, "value": newValue, info: publishInfo}
        );
        publishInfo.fieldId = field.id;
        internal.publish('field-value-change-byDef-' + field.fieldDefinition.id, publishInfo);

       _updateChildrenState();
    };

    /* eslint-disable-next-line complexity */
    const _init = function() {

        _extendField();

        view = FieldView(core, instance, field, _fieldComponent, {
            clone: _clone,
            removeDuplicate: function(fieldId){
                internal.publish("field-collection-decrement-" + fieldId, fieldId);
            },
            dropField: function(droppedFieldId) {
               callbacks.fieldDropped(droppedFieldId, field.id)
            }
        });

        view.init(function() {
            _setValue(view.getValue(), {
                id: field.fieldDefinition.id,
                value: view.getValue()
            });
        });

        if( typeof field.upToDate !== 'undefined' && !field.upToDate) view.showOutdated(true)

        if (_auto) {
            view.disableSelf();
        }

        if(field.viz){
            // we trigger the initial update when all the fields are ready to be found
            internal.subscribe('instance-ready-internal', _initViz);
        }

        if (field.fields && field.fields.length > 0) {
            view.showToggleButton();

            field.fields.forEach(function(childField) {
                if (childField.fieldDefinition.duplicable) {

                    if(!(childField.fieldDefinition.id in _dupPs)){
                        _dupPs[childField.fieldDefinition.id] = new DuplicateFieldsPresenter(
                            core, internal, view, instance, childField, this, opts, ignoreDefaults, clipboard,
                            {updateVisibility: _updateChildrenState}
                        );
                    } else {
                        _dupPs[childField.fieldDefinition.id].addDuplicate(null, childField);
                    }

                } else {
                    var newFieldP = new FieldPresenter(
                        core, internal, instance, childField, this, opts, ignoreDefaults, clipboard,
                        { updateVisibility: _updateChildrenState }
                    );
                    _fieldPs.push(newFieldP);
                    view.addChildField(newFieldP);
                }
            }.bind(this));

            _updateChildrenState();
        }

        if (field.fieldDefinition.duplicable && opts.edit && !field.fieldDefinition.isBlocked &&
                   (!field.fieldDefinition.required ||
                    (field.fieldDefinition.required && opts[field.fieldDefinition.required]))) {
            if (field.duplicate) {
                view.showRemoveButton(`scope-${field.fieldDefinition.id}`);
            } else {
                view.showDuplicateButton(`scope-${field.fieldDefinition.id}`);
            }
        }

        if (!opts.edit) {
            view.disableSelf();
        }

        internal.subscribe("collapse-all", function() {
            if (field.fields && field.fields.length > 0) view.colapseFields();
        });

        internal.subscribe("expand-all", function() {
            if (field.fields && field.fields.length > 0) view.expandFields();
        });

        if (_auto) {
            internal.subscribe('instance-ready-internal', _registerAutoFunction);
        }

        if (_expand) {
            internal.subscribe('instance-ready-internal', _applyExpands);
        }

        if (_mark) {
            internal.subscribe('instance-ready-internal', _applyMarkIf);
        }

        if (_readonlyConf) {
            internal.subscribe('instance-ready-internal', _applyReadonly);
        }

        if (_placeholders) {

            if(_isGroup){
                // $group field placeholders should be updated even if the field is marked as read only
                // because the placeholders are in the fields names, which are always shown

                if(opts.isNewDuplicate){
                    _initGroupPlaceholders();

                } else {
                    internal.subscribe('instance-ready-internal', _initGroupPlaceholders);
                }

            } else if(_isReferences){
                // $references field placeholders should be updated even if the field is marked as read only
                // because the placeholders are in their queries, which don't rely on any sort of input

                internal.subscribe('instance-ready-internal', _initRefsPlaceholders);

            } else if(!field.fieldDefinition.readOnly){
                // $ref and $extRef fields placeholders only get replaced if the fields are editable
                // because the placeholders are in their queries, which are only used when the user gives some input
                if(_isRef){

                    if(opts.isNewDuplicate){
                        _initRefPlaceholders();

                    } else {

                        internal.subscribe('instance-ready-internal', _initRefPlaceholders);
                    }

                } else if(_isExtRef){

                    if(opts.isNewDuplicate){
                        _initExtRefPlaceholders();

                    } else {

                        internal.subscribe('instance-ready-internal', _initExtRefPlaceholders);
                    }
                }
            }
        }

        if(_isCreateLinkField){
            internal.subscribe('instance-ready-internal', _fieldComponent.onInstanceReady);
        }

        if (field.fieldDefinition.readOnly) {
            internal.subscribe('instance-ready-internal', _applyReadonly);
        }

        view.colapseFields();

        if (opts.edit) {
            if (clipboard.hasContentFor(field.fieldDefinition.name)) {
                var newValue = clipboard.getValueFor(field.fieldDefinition.name);

                if(clipboard.autoPasteIfEmpty() && (!view.getValue() || view.getValue().length === 0)){
                    view.blink(2);
                    view.setValue(newValue);
                    _setValue(newValue);
                    if(clipboard.setReadonlyOnPaste()) view.disableSelf();
                }else{
                    view.showPasteButton(newValue, function() {
                        view.blink(1);
                        view.setValue(newValue);
                        _setValue(newValue);
                        if(clipboard.setReadonlyOnPaste()) view.disableSelf();
                    });
                }
            }
        }

        if (field.fieldDefinition.help) {
            view.addHelpPopover(field.fieldDefinition.id, field.fieldDefinition.help, field.fieldDefinition.name);
        }

    };

    var _applyAutoFunction = function() {
        var result = _autoFunction.apply(field.value);

        if (result.error) {
            view.showError(result.error);

        } else if (result.warning) {
            view.showError(result.warning);

        } else {
            if (view.getValue() !== result.value) {
                view.setValue(result.value);

                internal.publish('field-value-change-byDef-' + field.fieldDefinition.id, {
                    id: field.fieldDefinition.id,
                    fieldId: field.id,
                    value: result.value,
                    refmodel: _refModel
                });

                internal.publish('field-value-change-' + field.id, view.getValue());

                internal.publish('field-value-change-byName-' + encodeURIComponent(field.fieldDefinition.name),
                    {"name": field.fieldDefinition.name, "value": view.getValue()}
                );
            }

            // it it was updated server-side, we don't care if the client can't update
            field.upToDate = field.upToDate || !result.outdated;
            view.showOutdated(!field.upToDate)
        }
    };

    var _destroyFieldComponent = function() {
        if (_fieldComponent && _fieldComponent.destroy) {
            _fieldComponent.destroy();
        }
    };

    const exported = {
        // called only when it's a new duplicate of an existing field
        // to do what is done normally as a response to 'instance-ready-internal'
        initDuplicate: function(){
            if (_auto) _registerAutoFunction();
            if (_expand) _applyExpands();
            if (_mark) _applyMarkIf();
            if (_readonlyConf) _applyReadonly();

            if(_isCreateLinkField) _fieldComponent.onInstanceReady();

            if (field.fieldDefinition.readOnly) _applyReadonly();

            // fill new duplicates with their default value
            if (field.id < 0 && Boolean(_defaultValue) && !ignoreDefaults) _fillWithDefaultValue();

            if(field.viz) _initViz();

            _fieldPs.forEach(function(p){ p.initDuplicate()});
            _dupPs.forEach((d) => d.initDuplicate())
        },
        getId: function() {
            return field.id;
        },
        getParent: function() {
            return parent;
        },
        getField: function(){
            return field;
        },
        isField: true,
        field: field,
        findSibling: _findSibling,
        getChildPs: function(){
            return _fieldPs;
        },
        getChildDuPs: function(){
            return _dupPs;
        },
        getValue: function() {
            const viewVal = view.getValue();

            // For simple input fields, empty values should be considered undefined
            if(viewVal === ""){
                return undefined;
            }

            return viewVal;
        },
        setValue: function(value){
           view.setValue(value);
           _setValue(value);
        },
        // used by customizations
        enable: function(){
            view.enableSelf();

            if(field.duplicate){
                view.showRemoveButton(`scope-${field.fieldDefinition.id}`);
            } else if(field.fieldDefinition.duplicable){
                view.showDuplicateButton(`scope-${field.fieldDefinition.id}`);
            }
        },
        // used by customizations
        disable: function(){
           view.disableSelf();

           if(field.duplicate){
               view.hideRemoveButton();
           } else if(field.fieldDefinition.duplicable){
               view.hideDuplicateButton();
           }
        },
        expand: function (){
            view.expandFields();
            if(!_isRootField) parent.expand();
        },
        collapse: function (){
            view.colapseFields();
        },
        content: function() {
            return view.getContainer();
        },
        isVisible: function() {
            return _visible && (typeof parent.isVisible == 'undefined' || parent.isVisible() );
        },
        isNewlyVisible: function() {
            return _newlyVisible;
        },
        validate: function() {
            if (!_visible || !opts.validate ) {
                return true; // non-visible or disabled fields are not subjected to validation
            }

            var allOk = true;

            _fieldPs.forEach(function(fieldP) {
                allOk = fieldP.validate() && allOk;
            });
            _dupPs.forEach(function(dupP) {
                allOk = dupP.validate() && allOk;
            });

            if (!allOk) view.expandFields();

            return _validateSelf() && allOk;
        },
        getModel: function(withLocalIds) {

            var fieldModel = {};

            fieldModel.id = field.id;
            fieldModel.duplicate = field.duplicate;
            fieldModel.off = field.off;
            fieldModel.fieldDefinition = field.fieldDefinition;
            fieldModel.fieldDefinition.description = field.fieldDefinition.description || ''; // para não precisarmos de testar se existe
            fieldModel.fieldDefinition.configuration = field.fieldDefinition.configuration;
            if (typeof field.upToDate !== 'undefined') fieldModel.upToDate = field.upToDate;
            fieldModel.fields = [];

            if(!_isRootField){
                fieldModel.parent = parent.field.id;
            }

            _fieldPs.forEach(function(el) {
                fieldModel.fields.push(el.getModel(withLocalIds));
            });

            _dupPs.forEach(function(el) {
                el.fillModel(fieldModel, withLocalIds);
            });

            if (this.isVisible()) {
                fieldModel.value = view.getValue();
                fieldModel.searchValue = field.searchValue;
            } else {
                fieldModel.value = null;
            }
            if (field.id < 0 && !withLocalIds) {
                fieldModel.id = undefined;
                if (!_isRootField && parent.field.id < 0) fieldModel.parent = undefined;
            }

            return fieldModel;
        },
        presentErrors: function(errors) {
            view.clearErrors();
            _fieldComponent?.clearError()

            const myError = errors.find((f) => f.fieldId === field.id);
            if (myError) {
                window.console.log("SRVS: found error [" + (myError.type || '') + "] for field " + field.fieldDefinition.name);
                _fieldComponent?.showError()
                view.showError(myError);
                if(!_isRootField) parent.expand();
            }

            _fieldPs.forEach(function(fieldP) {
                fieldP.presentErrors(errors);
            });
            _dupPs.forEach(function(dupP) {
                dupP.presentErrors(errors);
            });
        },
        getClone: _getClone,
        destroy: function(sendDupFieldRemovedEventArg) {

            // Defaults to false for retro compatibility
            const sendDupFieldRemovedEvent = (sendDupFieldRemovedEventArg !== undefined
                ? sendDupFieldRemovedEventArg
                : false);

            _destroyFieldComponent();
            view.remove();
            internal.unsubscribe('instance-ready-internal');

           _fieldPs.forEach((child) => child.destroy());
           _dupPs.forEach((child) => child.destroy());

           if(field.duplicate && sendDupFieldRemovedEvent){
                internal.publish(`field-collection-decrement-${field.id}`, field.id);
           }
        }
    };

    _init.bind(exported)();
    return exported;
};

FieldPresenter.getPlaceholders = function(str = ""){
    // +? = lazy mode (https://javascript.info/regexp-greedy-and-lazy)
    return str.match(/__(_*.+?_*)__/g);
}

FieldPresenter.resolvePlaceholder = function (placeholder = ""){
    // Remove the first and last "__"
    return placeholder.replace(/^__|__$/g, "");
};

export default FieldPresenter;
